import axios, { AxiosResponse } from 'axios';

import { getConfig } from '../config';
import { CommunityPost, PostCategory } from '../database/community';
import { MediaUploadRequest, PresignedUrlResponse, SetLikeRequest } from './theSourceApi';

const BASE_URL = getConfig().api.baseUrl;

export type CommunityApiContextType = {
    /**
     * createCommunityPost saves the provided post in the database.
     * @param req The CommunityPostRequest.
     * @returns The newly created post.
     */
    createCommunityPost: (
        req: CommunityPostRequest,
    ) => Promise<AxiosResponse<CommunityPost, any>>;

    /**
     * deleteCommunityPost deletes the provided post in the database.
     * @param req The CommunityPostRequest.
     * @returns The newly created post.
     */
    deletePost: (post: CommunityPost) => Promise<AxiosResponse<CommunityPost, any>>;

    /**
     * listCommunityPosts returns a list of most recent posts.
     * @param startKey The optional startKey to use when searching.
     * @returns A list of the most recent community posts.
     */
    listPostsByCategory: (
        category: string,
        startKey?: string,
    ) => Promise<AxiosResponse<ListCommunityPostsResponse, any>>;

    /**
     * listCommunityPostsByOwner returns a list of posts for the signed in user.
     * @param startKey The optional startKey to use when searching.
     * @returns A list of community posts for the signed in user.
     */
    listCommunityPostsByOwner: (
        category: string,
        startKey?: string,
    ) => Promise<AxiosResponse<ListCommunityPostsResponse, any>>;

    /**
     * createComment adds the given content as a comment on the given post.
     * @param id The id of the post.
     * @param content The text content of the post.
     */
    createComment: (
        postOwner: string,
        postCreatedAt: string,
        content: string,
    ) => Promise<AxiosResponse<CommunityPost, any>>;

    communityPostGeneratePresignedUrl: (
        post: CommunityPost,
        contentType: string,
    ) => Promise<AxiosResponse<PresignedUrlResponse>>;

    uploadMediaToS3: (req: MediaUploadRequest) => Promise<AxiosResponse<string>>;

    /**
     * getPost fetches a single community post by owner and createdAt.
     * @param owner The owner of the post.
     * @param createdAt The creation timestamp of the post.
     * @returns The community post.
     */
    getPost: (
        owner: string,
        createdAt: string,
    ) => Promise<AxiosResponse<CommunityPost, any>>;

    /**
     * setCommunityPostLike adds or removes a like on the given post identified by the owner and createdAt timestamp.
     * @param owner The owner of the post.
     * @param createdAt The creation date of the post.
     * @param req The SetLikeRequest that contains if the post is liked.
     * @returns The updated post with the like added or removed.
     */
    setCommunityPostLike: (
        owner: string,
        createdAt: string,
        req: SetLikeRequest,
    ) => Promise<AxiosResponse<CommunityPost, any>>;

    /**
     * updateCommunityPost updates the content and media of an existing post.
     * @param owner The owner of the post.
     * @param createdAt The creation timestamp of the post.
     * @param req The update request.
     * @returns The updated post.
     */
    updateCommunityPost: (
        owner: string,
        createdAt: string,
        req: CommunityPostUpdateRequest,
    ) => Promise<AxiosResponse<CommunityPost, any>>;
};

export interface ListCommunityPostsResponse {
    posts: CommunityPost[];
    lastEvaluatedKey?: string;
}

export interface CommunityPostRequest {
    content: string;
    containsMedia: boolean;
    category: PostCategory;
}

export interface CommunityPostUpdateRequest {
    content: string;
    containsMedia: boolean;
    removeMedia: boolean;
}

export function createCommunityPost(idToken: string, req: CommunityPostRequest) {
    return axios.post<CommunityPost>(BASE_URL + '/community/post/create', req, {
        headers: {
            Authorization: 'Bearer ' + idToken,
        },
    });
}

export function deletePost(idToken: string, post: CommunityPost) {
    return axios.delete<CommunityPost>(
        BASE_URL + `/community/post/delete/${post.owner}/${post.createdAt}`,
        {
            headers: {
                Authorization: 'Bearer ' + idToken,
            },
        },
    );
}

export async function listPostsByCategory(category: string, startKey?: string) {
    return axios.get<ListCommunityPostsResponse>(
        BASE_URL + `/public/community/posts/${category}`,
        {
            params: { startKey },
        },
    );
}

export function listCommunityPostsByOwner(
    idToken: string,
    category: string,
    startKey?: string,
) {
    return axios.get<ListCommunityPostsResponse>(
        BASE_URL + `/community/my-posts/${category}`,
        {
            params: { startKey },
            headers: {
                Authorization: 'Bearer ' + idToken,
            },
        },
    );
}

/**
 * createComment adds the given content as a comment on the given post.
 * @param id The id of the post.
 * @param content The text content of the post.
 */
export function createComment(
    idToken: string,
    postOwner: string,
    createdAt: string,
    content: string,
) {
    return axios.post<CommunityPost>(
        BASE_URL + `/post/${postOwner}/${createdAt}/comment`,
        { content },
        {
            headers: {
                Authorization: 'Bearer ' + idToken,
            },
        },
    );
}

export function communityPostGeneratePresignedUrl(
    idToken: string,
    post: CommunityPost,
    contentType: string,
) {
    return axios.post(
        `${BASE_URL}/post/presign-url`,
        { post, contentType },
        {
            headers: {
                Authorization: 'Bearer ' + idToken,
            },
        },
    );
}

export function uploadMediaToS3(req: MediaUploadRequest) {
    return axios.put(req.presignedUrl, req.mediaContent, {
        headers: {
            'Content-Type': req.contentType,
        },
        onUploadProgress: req.onUploadProgress,
    });
}

export function getPost(owner: string, createdAt: string) {
    return axios.get<CommunityPost>(
        BASE_URL + `/public/community/post/${owner}/${createdAt}`,
    );
}

export function updateCommunityPost(
    idToken: string,
    owner: string,
    createdAt: string,
    req: CommunityPostUpdateRequest,
) {
    return axios.put<CommunityPost>(
        BASE_URL + `/community/post/update/${owner}/${createdAt}`,
        req,
        {
            headers: {
                Authorization: 'Bearer ' + idToken,
            },
        },
    );
}

export function setCommunityPostLike(
    idToken: string,
    owner: string,
    createdAt: string,
    req: SetLikeRequest,
) {
    return axios.put<CommunityPost>(`${BASE_URL}/post/${owner}/${createdAt}/like`, req, {
        headers: {
            Authorization: 'Bearer ' + idToken,
        },
    });
}
